import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Phone, Settings, CheckCircle2, AlertTriangle, Play, Pause,
  Save, Mic, Volume2, Webhook, RefreshCw, PhoneCall, PhoneIncoming,
  Bell, User, Sparkles,
} from "lucide-react";

const DEFAULT_VOICES_QC = [
  // Voix recommandées par défaut (cocktail de voix françaises naturelles ElevenLabs)
  { id: "XB0fDUnXU5powFXDhCwa", name: "Charlotte", description: "Voix féminine douce, FR/EN multilingue", recommended: true },
  { id: "ThT5KcBeYPX3keUQqHPh", name: "Dorothy", description: "Féminine chaleureuse" },
  { id: "FGY2WhTYpPnrIDTdsKH5", name: "Laura", description: "Féminine pro" },
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Sarah", description: "Féminine claire" },
  { id: "Xb7hH8MSUJpSbSDYk0k2", name: "Alice", description: "Féminine UK/multi" },
];

export default function CamillePhoneModule() {
  const [config, setConfig] = useState(null);
  const [calls, setCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [testText, setTestText] = useState("Bonjour, vous êtes à l'Institut Impact. Je suis Camille, l'assistante virtuelle de Kru Ralph. Comment puis-je vous aider?");
  const [testingVoice, setTestingVoice] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [activeTab, setActiveTab] = useState("leads"); // leads | calls | voice | config
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, l, s, ld] = await Promise.all([
        api.get("/camille/voice/config"),
        api.get("/camille/voice/calls?limit=30"),
        api.get("/camille/voice/stats"),
        api.get("/camille/voice/leads-pending"),
      ]);
      setConfig(c.data);
      setCalls(l.data);
      setStats(s.data);
      setLeads(ld.data);
    } catch (e) {
      toast.error("Erreur chargement");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    try {
      const { data } = await api.put("/camille/voice/config", patch);
      setConfig({ ...data, twilio_configured: config.twilio_configured,
                  elevenlabs_configured: config.elevenlabs_configured,
                  twilio_phone_number: config.twilio_phone_number,
                  public_webhook_url: config.public_webhook_url });
      toast.success("Configuration sauvegardée");
    } catch { toast.error("Échec sauvegarde"); }
  };

  const testVoice = async (voiceId) => {
    setTestingVoice(true);
    setAudioUrl(null);
    try {
      const resp = await api.post(
        "/camille/voice/test-elevenlabs",
        { text: testText, voice_id: voiceId || config.voice_id },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      setAudioUrl(url);
      // Auto-play
      setTimeout(() => {
        if (audioRef.current) audioRef.current.play();
      }, 100);
    } catch (e) {
      const msg = e?.response?.data ? "ElevenLabs : crédits/permission manquant" : "Erreur génération";
      toast.error(msg);
    } finally { setTestingVoice(false); }
  };

  const configureTwilioWebhook = async () => {
    setConfiguring(true);
    try {
      const { data } = await api.post("/camille/voice/configure-twilio-webhook");
      toast.success(`Webhook configuré : ${data.voice_url}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Configuration échouée");
    } finally { setConfiguring(false); }
  };

  const markCalledBack = async (callSid) => {
    try {
      await api.post(`/camille/voice/calls/${callSid}/mark-called-back`);
      toast.success("Marqué comme rappelé");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const extractLead = async (callSid) => {
    try {
      const { data } = await api.post(`/camille/voice/calls/${callSid}/extract-lead`);
      if (data.lead?.has_lead) toast.success("Lead extrait!");
      else toast.info("Pas de lead détecté dans cet appel");
      await load();
    } catch { toast.error("Erreur extraction"); }
  };

  if (!config) return <div className="p-8 text-zinc-400">Chargement Camille Téléphone...</div>;

  const twilioOK = config.twilio_configured;
  const elOK = config.elevenlabs_configured;
  const allReady = twilioOK && elOK && config.enabled;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="camille-phone-module">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
            Camille — Téléphone 24/7
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Réceptionniste vocale IA · Bilingue FR-CA / EN · {stats?.total_calls || 0} appels traités
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <Badge ok={twilioOK} label={`Twilio ${twilioOK ? "OK" : "à compléter"}`} />
          <Badge ok={elOK} label={`ElevenLabs ${elOK ? "OK" : "à compléter"}`} />
          <Badge ok={config.enabled} label={config.enabled ? "Active" : "En pause"} />
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Appels totaux" value={stats?.total_calls || 0} color="text-white" icon={Phone} />
        <StatCard label="Terminés" value={stats?.completed || 0} color="text-emerald-300" icon={CheckCircle2} />
        <StatCard label="En cours" value={stats?.active || 0} color="text-blue-300" icon={PhoneIncoming} />
        <StatCard label="Leads captés" value={stats?.leads_captured || 0} color="text-amber-300" icon={Sparkles} />
        <StatCard label="À RAPPELER" value={stats?.pending_callbacks || 0} color="text-orange-400" icon={Bell} pulse={(stats?.pending_callbacks || 0) > 0} />
      </div>

      {/* 🔔 RAPPELS EN ATTENTE (en haut, priorité visuelle) */}
      {leads.length > 0 && (
        <div className="mb-6 rounded-lg border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-amber-500/5 p-5" data-testid="pending-callbacks-panel">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-orange-400 animate-pulse" />
            <h2 className="text-base font-bold text-white">
              {leads.length} {leads.length === 1 ? "personne à rappeler" : "personnes à rappeler"}
            </h2>
          </div>
          <div className="space-y-2">
            {leads.map((c) => {
              const lead = c.captured_lead || {};
              const phone = lead.phone || c.from_number || "—";
              const cleanPhone = phone.replace(/[^\d+]/g, "");
              const priority = lead.callback_priority || "medium";
              const prColor = priority === "high" ? "bg-red-500/20 text-red-300 border-red-500/40"
                : priority === "low" ? "bg-zinc-700/40 text-zinc-300 border-zinc-600"
                : "bg-amber-500/20 text-amber-300 border-amber-500/40";
              return (
                <div key={c.call_sid} className="p-4 rounded-lg bg-zinc-950 border border-orange-500/20" data-testid={`callback-${c.call_sid}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-sm font-semibold text-white">
                          {lead.name || "Nom non capté"}
                          {lead.child_age && <span className="text-zinc-400 font-normal"> · enfant {lead.child_age} ans</span>}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${prColor}`}>{priority}</span>
                        {lead.program_interest && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">
                            {lead.program_interest}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-300 ml-5">{lead.summary || lead.intent}</div>
                      <div className="flex items-center gap-3 mt-1 ml-5">
                        <a href={`tel:${cleanPhone}`} className="text-sm font-mono text-orange-300 hover:text-orange-200" data-testid={`call-tel-${c.call_sid}`}>
                          📞 {phone}
                        </a>
                        <span className="text-[11px] text-zinc-500">
                          {new Date(c.started_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={`tel:${cleanPhone}`}
                        data-testid={`call-btn-${c.call_sid}`}
                        className="px-3 py-1.5 rounded bg-orange-500 hover:bg-orange-400 text-white text-xs font-semibold flex items-center gap-1.5"
                      >
                        <PhoneCall className="w-3.5 h-3.5" /> Rappeler
                      </a>
                      <button
                        data-testid={`done-btn-${c.call_sid}`}
                        onClick={() => markCalledBack(c.call_sid)}
                        className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs flex items-center gap-1.5"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Fait
                      </button>
                    </div>
                  </div>
                  {c.transcript && c.transcript.length > 0 && (
                    <details className="mt-2 ml-5">
                      <summary className="text-[11px] text-zinc-400 cursor-pointer hover:text-white">
                        Voir la transcription complète ({c.transcript.length} tours)
                      </summary>
                      <div className="mt-2 p-3 bg-zinc-900 rounded space-y-1">
                        {c.transcript.map((t, i) => (
                          <div key={i} className={`text-xs ${t.role === "user" ? "text-blue-200" : "text-pink-200"}`}>
                            <span className="font-semibold">{t.role === "user" ? "Appelant" : "Camille"} :</span> {t.text}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* INFO PRINCIPAL */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-5 mb-6">
        <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Webhook className="w-4 h-4 text-violet-400" /> Configuration Twilio
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <InfoRow label="Numéro acheté" value={config.twilio_phone_number || "—"} testId="phone-number" />
          <InfoRow label="Webhook public" value={config.public_webhook_url || "—"} testId="webhook-url" mono />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            data-testid="configure-twilio-btn"
            disabled={configuring || !twilioOK}
            onClick={configureTwilioWebhook}
            className="px-4 py-2 rounded-lg bg-violet-500/20 border border-violet-500/40 text-violet-200 text-xs disabled:opacity-50 hover:bg-violet-500/30 flex items-center gap-2"
          >
            <Webhook className="w-3.5 h-3.5" />
            {configuring ? "Configuration..." : "Configurer le webhook sur Twilio"}
          </button>
          <button
            data-testid="toggle-phone-enabled"
            onClick={() => saveConfig({ enabled: !config.enabled })}
            className={`px-4 py-2 rounded-lg text-xs font-semibold ${config.enabled ? "bg-zinc-800 text-zinc-300" : "bg-emerald-500 text-white"}`}
          >
            {config.enabled ? "Mettre en pause" : "Activer Camille"}
          </button>
        </div>
        {!twilioOK && (
          <div className="mt-3 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
            ⚠️ Le <code>TWILIO_AUTH_TOKEN</code> est manquant dans le backend <code>.env</code>. Ralph doit me le donner pour que Camille téléphone fonctionne.
          </div>
        )}
      </div>

      {/* VOIX */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-5 mb-6">
        <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-pink-400" /> Voix de Camille (ElevenLabs)
        </h2>

        <div className="mb-4">
          <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Texte de test</label>
          <textarea
            data-testid="test-text-input"
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {DEFAULT_VOICES_QC.map((v) => (
            <div
              key={v.id}
              className={`p-3 rounded border cursor-pointer transition ${
                config.voice_id === v.id
                  ? "border-pink-500/50 bg-pink-500/10"
                  : "border-zinc-700 bg-zinc-900 hover:border-zinc-600"
              }`}
              onClick={() => saveConfig({ voice_id: v.id })}
              data-testid={`voice-card-${v.name}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-semibold text-white flex items-center gap-2">
                  {v.name}
                  {v.recommended && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">recommandée</span>}
                </div>
                {config.voice_id === v.id && <CheckCircle2 className="w-4 h-4 text-pink-400" />}
              </div>
              <div className="text-xs text-zinc-400 mb-2">{v.description}</div>
              <button
                data-testid={`voice-test-${v.name}`}
                onClick={(e) => { e.stopPropagation(); testVoice(v.id); }}
                disabled={testingVoice || !elOK}
                className="px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-xs text-white flex items-center gap-1.5 disabled:opacity-50"
              >
                {testingVoice ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Écouter
              </button>
            </div>
          ))}
        </div>

        {audioUrl && (
          <div className="p-3 rounded bg-zinc-900 border border-zinc-700">
            <div className="text-xs text-zinc-400 mb-2">🔊 Lecteur audio Camille</div>
            <audio ref={audioRef} controls src={audioUrl} className="w-full" data-testid="voice-player" />
          </div>
        )}
      </div>

      {/* GREETING */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-5 mb-6">
        <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Mic className="w-4 h-4 text-blue-400" /> Salutation d'accueil
        </h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Français (défaut)</label>
            <input
              data-testid="greeting-fr"
              value={config.welcome_greeting_fr || ""}
              onChange={(e) => setConfig({ ...config, welcome_greeting_fr: e.target.value })}
              onBlur={() => saveConfig({ welcome_greeting_fr: config.welcome_greeting_fr })}
              className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Anglais (si appelant anglophone)</label>
            <input
              data-testid="greeting-en"
              value={config.welcome_greeting_en || ""}
              onChange={(e) => setConfig({ ...config, welcome_greeting_en: e.target.value })}
              onBlur={() => saveConfig({ welcome_greeting_en: config.welcome_greeting_en })}
              className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
            />
          </div>
        </div>
      </div>

      {/* JOURNAL DES APPELS */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Journal des appels</h2>
          <button onClick={load} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Actualiser
          </button>
        </div>
        {calls.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            Aucun appel reçu pour l'instant. Une fois Camille active, les appels apparaîtront ici en temps réel.
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {calls.map((c) => (
              <CallRow key={c.call_sid} call={c} onExtract={extractLead} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CallRow({ call, onExtract }) {
  const [expanded, setExpanded] = useState(false);
  const status = call.status || "active";
  const color = status === "completed" ? "text-emerald-400"
    : status === "active" ? "text-blue-400"
    : status === "transferred" ? "text-yellow-400"
    : "text-zinc-500";
  const hasLead = !!call.captured_lead;
  return (
    <div className="p-4 hover:bg-zinc-900/40 transition" data-testid={`call-${call.call_sid}`}>
      <div className="flex items-start gap-3">
        <Phone className={`w-4 h-4 mt-1 ${color}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm text-white">{call.from_number || "Numéro inconnu"}</span>
            <span className={`text-xs ${color}`}>{status}</span>
            {hasLead && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">LEAD</span>}
            {call.called_back && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">✓ rappelé</span>}
            <span className="text-xs text-zinc-500 ml-auto">
              {new Date(call.started_at).toLocaleString("fr-CA")}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            {call.transcript && call.transcript.length > 0 && (
              <button onClick={() => setExpanded(!expanded)} className="text-xs text-zinc-400 hover:text-white">
                {expanded ? "Masquer" : "Voir"} transcription ({call.transcript.length})
              </button>
            )}
            {!hasLead && call.transcript && call.transcript.length >= 2 && (
              <button
                data-testid={`extract-${call.call_sid}`}
                onClick={() => onExtract(call.call_sid)}
                className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" /> Extraire lead
              </button>
            )}
          </div>
          {expanded && (
            <div className="mt-2 space-y-1.5 max-h-64 overflow-y-auto p-3 bg-zinc-900 rounded">
              {call.transcript.map((t, i) => (
                <div key={i} className={`text-xs ${t.role === "user" ? "text-blue-200" : "text-pink-200"}`}>
                  <span className="font-semibold">{t.role === "user" ? "Appelant" : "Camille"} :</span> {t.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ ok, label }) {
  return (
    <span className={`px-2 py-1 rounded text-[11px] font-semibold ${ok ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-700/40 text-zinc-400"}`}>
      {ok ? "✓" : "—"} {label}
    </span>
  );
}

function StatCard({ label, value, color, icon: Icon, pulse }) {
  return (
    <div className={`p-4 rounded-lg border ${pulse ? "border-orange-500/50 bg-orange-500/10 animate-pulse" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-zinc-500">{label}</div>
          <div className={`text-2xl font-bold ${color} mt-1`}>{value}</div>
        </div>
        <Icon className="w-5 h-5 text-zinc-600" />
      </div>
    </div>
  );
}

function InfoRow({ label, value, testId, mono }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      <div data-testid={testId} className={`text-sm text-white ${mono ? "font-mono text-xs" : ""} truncate`}>{value}</div>
    </div>
  );
}
